import { useCallback, useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { WifiOff, Wifi, RefreshCw } from "lucide-react";
import { Capacitor } from "@capacitor/core";
import { logger } from "@/lib/logger";

const TAG = "[OfflineOverlay]";

const STARTUP_VERIFY_DELAY_MS = 800;
const RESTORE_VERIFY_DELAY_MS = 800;
const RESTORE_RETRY_DELAY_MS = 1000;
const HEARTBEAT_MS = 5000;
const HEARTBEAT_MAX_ATTEMPTS = 36;
const PING_TIMEOUT_MS = 2500;

const isNative = Capacitor.isNativePlatform();

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

export function OfflineOverlay() {
  const [isOffline, setIsOffline] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [showRestored, setShowRestored] = useState(false);

  const isOfflineRef = useRef(false);
  const verifyingRef = useRef(false);
  const heartbeatRef = useRef<number | null>(null);
  const heartbeatCountRef = useRef(0);
  const restoreTimerRef = useRef<number | null>(null);
  const restoredBannerTimerRef = useRef<number | null>(null);

  /* ── Timer helpers ──────────────────────────────────────────────────── */

  const clearRestoreTimer = () => {
    if (restoreTimerRef.current) {
      window.clearTimeout(restoreTimerRef.current);
      restoreTimerRef.current = null;
    }
  };

  const clearRestoredBannerTimer = () => {
    if (restoredBannerTimerRef.current) {
      window.clearTimeout(restoredBannerTimerRef.current);
      restoredBannerTimerRef.current = null;
    }
  };

  const clearHeartbeat = () => {
    if (heartbeatRef.current) {
      window.clearInterval(heartbeatRef.current);
      heartbeatRef.current = null;
    }
  };

  /* ── Haptics ────────────────────────────────────────────────────────── */

  const fireHaptic = useCallback(async (type: "drop" | "restore") => {
    if (!isNative) return;
    try {
      const { Haptics, NotificationType } = await import("@capacitor/haptics");
      await Haptics.notification({
        type:
          type === "drop"
            ? NotificationType.Warning
            : NotificationType.Success,
      });
    } catch {
      /* no-op */
    }
  }, []);

  /* ── Ping verification ──────────────────────────────────────────────── */

  const verifyConnectivity = useCallback(async (): Promise<boolean> => {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      console.warn(`${TAG} VITE_SUPABASE_URL missing, assuming online`);
      return true;
    }

    const url = `${supabaseUrl}/functions/v1/ping?t=${Date.now()}`;
    try {
      const controller = new AbortController();
      const timeout = window.setTimeout(
        () => controller.abort(),
        PING_TIMEOUT_MS
      );
      const res = await fetch(url, {
        method: "GET",
        cache: "no-store",
        signal: controller.signal,
      });
      window.clearTimeout(timeout);
      const ok = res.ok || res.status === 204;
      logger.log(`${TAG} ping -> status=${res.status} ok=${ok}`);
      return ok;
    } catch (err) {
      console.warn(`${TAG} ping FAILED`, err);
      return false;
    }
  }, []);

  /* ── State transitions ──────────────────────────────────────────────── */

  const goOffline = useCallback(
    async (reason: string) => {
      if (isOfflineRef.current) return;
      logger.log(`${TAG} GO OFFLINE (${reason})`);
      isOfflineRef.current = true;
      setIsOffline(true);
      clearRestoreTimer();
      clearRestoredBannerTimer();
      setShowRestored(false);
      await fireHaptic("drop");
    },
    [fireHaptic]
  );

  const completeOnlineRestore = useCallback(async () => {
    logger.log(`${TAG} RESTORE ONLINE`);
    isOfflineRef.current = false;
    setIsOffline(false);
    clearHeartbeat();

    clearRestoredBannerTimer();
    setShowRestored(true);
    restoredBannerTimerRef.current = window.setTimeout(() => {
      setShowRestored(false);
      restoredBannerTimerRef.current = null;
    }, 2500);

    await fireHaptic("restore");
  }, [fireHaptic]);

  const goOnline = useCallback(
    async (reason: string) => {
      if (!isOfflineRef.current || verifyingRef.current) return;

      verifyingRef.current = true;
      try {
        logger.log(`${TAG} GO ONLINE VERIFY (${reason})`);
        let ok = await verifyConnectivity();
        logger.log(`${TAG} verify #1 = ${ok}`);

        if (!ok) {
          await sleep(RESTORE_RETRY_DELAY_MS);
          ok = await verifyConnectivity();
          logger.log(`${TAG} verify #2 = ${ok}`);
        }

        if (!ok) return;
        await completeOnlineRestore();
      } finally {
        verifyingRef.current = false;
      }
    },
    [verifyConnectivity, completeOnlineRestore]
  );

  const scheduleRestoreVerification = useCallback(
    (reason: string) => {
      clearRestoreTimer();
      restoreTimerRef.current = window.setTimeout(() => {
        restoreTimerRef.current = null;
        void goOnline(reason);
      }, RESTORE_VERIFY_DELAY_MS);
    },
    [goOnline]
  );

  /* ── 1) Startup check ───────────────────────────────────────────────── */

  useEffect(() => {
    const timer = window.setTimeout(async () => {
      const ok = await verifyConnectivity();
      logger.log(`${TAG} startup verify = ${ok}`);
      if (!ok) {
        console.warn(`${TAG} startup ping failed — retrying in 1.5s`);
        await sleep(1500);
        const retry = await verifyConnectivity();
        logger.log(`${TAG} startup retry = ${retry}`);
        if (!retry) void goOffline("startup");
      }
    }, STARTUP_VERIFY_DELAY_MS);

    return () => window.clearTimeout(timer);
  }, [verifyConnectivity, goOffline]);

  /* ── 2) Native Network plugin ───────────────────────────────────────── */

  useEffect(() => {
    if (!isNative) return;

    let removeListener: (() => Promise<void>) | null = null;

    (async () => {
      try {
        const { Network } = await import("@capacitor/network");
        const handle = await Network.addListener(
          "networkStatusChange",
          (status) => {
            logger.log(
              `${TAG} networkStatusChange connected=${status.connected} type=${status.connectionType}`
            );
            if (!status.connected) {
              void goOffline("network-plugin");
            } else {
              scheduleRestoreVerification("network-plugin");
            }
          }
        );
        removeListener = handle.remove;
      } catch (err) {
        console.warn(`${TAG} Network listener failed`, err);
      }
    })();

    return () => {
      if (removeListener) void removeListener();
    };
  }, [goOffline, scheduleRestoreVerification]);

  /* ── 3) App foreground re-check ─────────────────────────────────────── */

  useEffect(() => {
    if (!isNative) return;

    let removeListener: (() => Promise<void>) | null = null;

    (async () => {
      try {
        const { App } = await import("@capacitor/app");
        const handle = await App.addListener(
          "appStateChange",
          ({ isActive }) => {
            logger.log(`${TAG} appStateChange isActive=${isActive}`);
            if (!isActive) return;

            if (isOfflineRef.current) {
              scheduleRestoreVerification("app-resume-offline");
            } else {
              window.setTimeout(async () => {
                const ok = await verifyConnectivity();
                logger.log(`${TAG} foreground verify = ${ok}`);
                if (!ok) void goOffline("app-resume-verify");
              }, 300);
            }
          }
        );
        removeListener = handle.remove;
      } catch (err) {
        console.warn(`${TAG} App listener failed`, err);
      }
    })();

    return () => {
      if (removeListener) void removeListener();
    };
  }, [verifyConnectivity, goOffline, scheduleRestoreVerification]);

  /* ── 4) Web-only fallback ───────────────────────────────────────────── */

  useEffect(() => {
    if (isNative) return;

    const onOffline = () => void goOffline("browser-offline");
    const onOnline = () => scheduleRestoreVerification("browser-online");

    window.addEventListener("offline", onOffline);
    window.addEventListener("online", onOnline);

    return () => {
      window.removeEventListener("offline", onOffline);
      window.removeEventListener("online", onOnline);
    };
  }, [goOffline, scheduleRestoreVerification]);

  /* ── 5) Heartbeat while offline ─────────────────────────────────────── */

  useEffect(() => {
    clearHeartbeat();
    if (!isOffline) return;

    heartbeatCountRef.current = 0;
    logger.log(`${TAG} start heartbeat every ${HEARTBEAT_MS}ms`);

    heartbeatRef.current = window.setInterval(async () => {
      if (!isOfflineRef.current || verifyingRef.current) return;

      heartbeatCountRef.current += 1;
      logger.log(
        `${TAG} heartbeat #${heartbeatCountRef.current}/${HEARTBEAT_MAX_ATTEMPTS}`
      );

      if (heartbeatCountRef.current >= HEARTBEAT_MAX_ATTEMPTS) {
        logger.log(`${TAG} heartbeat exhausted`);
        clearHeartbeat();
        return;
      }

      await goOnline("heartbeat");
    }, HEARTBEAT_MS);

    return clearHeartbeat;
  }, [isOffline, goOnline]);

  /* ── Manual retry ───────────────────────────────────────────────────── */

  const handleRetry = useCallback(async () => {
    heartbeatCountRef.current = 0;
    setIsRetrying(true);
    try {
      await goOnline("manual-retry");
    } finally {
      setIsRetrying(false);
    }
  }, [goOnline]);

  /* ── Cleanup ────────────────────────────────────────────────────────── */

  useEffect(() => {
    return () => {
      clearHeartbeat();
      clearRestoreTimer();
      clearRestoredBannerTimer();
    };
  }, []);

  /* ── UI ──────────────────────────────────────────────────────────────── */

  return (
    <>
      <AnimatePresence>
        {isOffline && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-background/98 backdrop-blur-xl"
            style={{
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 20 }}
              transition={{
                duration: 0.5,
                delay: 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="flex flex-col items-center text-center px-8 max-w-xs"
            >
              <motion.div
                animate={{ y: [0, -6, 0] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative w-24 h-24 mb-8"
              >
                <div className="absolute inset-0 rounded-full border border-primary/20" />
                <div className="absolute inset-[6px] rounded-full border border-primary/10" />
                <div className="absolute inset-0 rounded-full flex items-center justify-center">
                  <WifiOff className="w-9 h-9 text-primary/70" />
                </div>
              </motion.div>

              <h2 className="text-xl font-semibold tracking-tight text-foreground mb-2">
                No Connection
              </h2>
              <p className="text-sm text-muted-foreground mb-10 leading-relaxed">
                Please check your internet connection and try again
              </p>

              <button
                onClick={handleRetry}
                disabled={isRetrying}
                className="group relative inline-flex items-center justify-center gap-2.5 px-8 py-3 text-sm font-medium tracking-wide uppercase text-primary border border-primary/30 rounded-full transition-all duration-300 hover:bg-primary/5 hover:border-primary/50 active:scale-[0.97] disabled:opacity-50 disabled:pointer-events-none"
              >
                <RefreshCw
                  className={`w-3.5 h-3.5 ${
                    isRetrying
                      ? "animate-spin"
                      : "group-hover:rotate-45 transition-transform duration-300"
                  }`}
                />
                {isRetrying ? "Checking…" : "Try Again"}
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showRestored && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[10000] flex items-center justify-center bg-background/98 backdrop-blur-xl"
            style={{
              paddingTop: "env(safe-area-inset-top)",
              paddingBottom: "env(safe-area-inset-bottom)",
            }}
          >
            <motion.div
              initial={{ scale: 0.92, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.92, opacity: 0, y: 20 }}
              transition={{
                duration: 0.5,
                delay: 0.1,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="flex flex-col items-center text-center px-8 max-w-xs"
            >
              {/* Icon with rings and glow */}
              <motion.div
                animate={{ scale: [1, 1.03, 1] }}
                transition={{
                  duration: 3,
                  repeat: Infinity,
                  ease: "easeInOut",
                }}
                className="relative w-24 h-24 mb-8"
              >
                {/* Ambient gold glow */}
                <motion.div
                  className="absolute inset-[-24px] rounded-full"
                  style={{
                    background:
                      "radial-gradient(circle, hsl(var(--gold) / 0.15) 0%, transparent 70%)",
                  }}
                  animate={{
                    scale: [1, 1.15, 1],
                    opacity: [0.5, 0.9, 0.5],
                  }}
                  transition={{
                    duration: 3,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <div className="absolute inset-0 rounded-full border border-gold/25" />
                <div className="absolute inset-[6px] rounded-full border border-gold/15" />
                <div className="absolute inset-0 rounded-full flex items-center justify-center">
                  <Wifi className="w-9 h-9 text-gold/80" />
                </div>
                {/* Animated checkmark */}
                <motion.div
                  initial={{ scale: 0, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ delay: 0.4, duration: 0.3, ease: "backOut" }}
                  className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-gold flex items-center justify-center shadow-[0_0_12px_hsl(var(--gold)/0.4)]"
                >
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 14 14"
                    fill="none"
                    className="text-charcoal"
                  >
                    <motion.path
                      d="M3 7.5L5.5 10L11 4"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      initial={{ pathLength: 0 }}
                      animate={{ pathLength: 1 }}
                      transition={{ delay: 0.6, duration: 0.4 }}
                    />
                  </svg>
                </motion.div>
              </motion.div>

              <h2 className="text-xl font-semibold tracking-tight text-foreground mb-2">
                Connected
              </h2>
              <p className="text-sm text-muted-foreground mb-8 leading-relaxed">
                You're back online
              </p>

              {/* Gold shimmer line */}
              <div className="flex items-center gap-2">
                <motion.div
                  className="h-px rounded-full"
                  style={{ background: "hsl(var(--gold) / 0.4)", width: 24 }}
                  animate={{ width: [16, 32, 16] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                  }}
                />
                <span
                  className="text-[10px] font-medium tracking-[0.35em] uppercase"
                  style={{ color: "hsl(var(--ivory) / 0.35)" }}
                >
                  Exora Club
                </span>
                <motion.div
                  className="h-px rounded-full"
                  style={{ background: "hsl(var(--gold) / 0.4)", width: 24 }}
                  animate={{ width: [16, 32, 16] }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: "easeInOut",
                    delay: 1.25,
                  }}
                />
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
